// Universal report controls: branches (all/one/many) · date presets/custom · Opening Balance ON/OFF · Excel/PDF/CSV.
// The SAME selection drives the data, totals, drill-downs and exports of the page using it.
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { Switch } from '@/components/ui/switch';
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const monthRange = (d = new Date()) => ({ start: iso(new Date(d.getFullYear(), d.getMonth(), 1)), end: iso(d) });

// Shared state for ReportFilterBar: permitted branches, selection, date range and ready-made API params.
export function useReportFilters({ withUnassigned = false, initialRange } = {}) {
  const [branches, setBranches] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [range, setRange] = useState(initialRange || monthRange());
  useEffect(() => {
    api.get('/branches').then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.branches || r.data?.items || []);
      setBranches(withUnassigned ? [...list, { id: '__unassigned__', name: 'Unassigned (no branch)' }] : list);
    }).catch(() => {});
  }, [withUnassigned]);
  const params = useMemo(() => ({
    ...(range?.start ? { start_date: range.start } : {}), ...(range?.end ? { end_date: range.end } : {}),
    ...(branchIds.length ? { branch_ids: branchIds.join(',') } : {}),
  }), [range, branchIds]);
  const branchLabel = branchIds.length ? branches.filter((b) => branchIds.includes(b.id)).map((b) => b.name).join(', ') : 'All permitted branches';
  return { branches, branchIds, setBranchIds, range, setRange, params, branchLabel };
}

export function ReportFilterBar({ branches, branchIds, onBranchIds, range, onRange, defaultPreset = 'month',
  opening, onOpening, exportSpec, exportDisabled, testId = 'report-filter', allowAll = false, children }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <DateQuickFilter hideAll={!allowAll} defaultActive={defaultPreset} branchIds={branchIds}
        onFilterChange={(r) => { if (r?.start && r?.end) onRange(r); else if (allowAll && !r) onRange(null); }} />
      <span className="text-[11px] text-muted-foreground font-mono" data-testid={`${testId}-range`}>{range?.start ? `${range.start} → ${range.end}` : 'All dates'}</span>
      <BranchMultiSelect value={branchIds} onChange={onBranchIds} branches={branches} testid={`${testId}-branches`} />
      {onOpening && (
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" data-testid={`${testId}-opening`}>
          <Switch checked={!!opening} onCheckedChange={onOpening} data-testid={`${testId}-opening-switch`} />Opening balance
        </label>
      )}
      {exportSpec && <ExportTrio spec={exportSpec} disabled={exportDisabled} testId={`${testId}-export`} />}
      {children}
    </div>
  );
}
